/**
 * Accordion Management Hook
 *
 * Refactored version of useAccordionState that uses AccordionManagementService
 * for centralized state management with caching and performance optimizations.
 *
 * Maintains 100% backward compatibility with the original useAccordionState interface
 * while leveraging the new AccordionManagementService for improved testability and reusability.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../reduxService/hooks';
import { setAccordionToOpen, selectAccordionToOpen } from '../store/form/formControlSlice';
import { AccordionManagementService, AccordionState } from '../services/AccordionManagementService';
import { logDebug } from '../utils/utils';

// Hook return type for backward compatibility
interface UseAccordionManagementReturn {
    openAccordions: AccordionState;
    handleAccordionToggle: (groupName: string) => void;
}

/**
 * Custom hook for accordion state management using AccordionManagementService
 *
 * This hook replaces the original useAccordionState and provides the same interface
 * while using the new centralized AccordionManagementService for improved performance
 * and testability.
 *
 * @returns Object with openAccordions state and handleAccordionToggle function
 */
export const useAccordionManagement = (): UseAccordionManagementReturn => {
    // Get singleton service instance
    const accordionService = useMemo(() => AccordionManagementService.getInstance(), []);

    // Local state synchronized with service
    const [openAccordions, setOpenAccordions] = useState<AccordionState>(() => accordionService.getAccordionState());

    // Redux integration - same as original useAccordionState
    const accordionToOpen = useAppSelector(selectAccordionToOpen);
    const dispatch = useAppDispatch();

    // Memoize Redux dependencies to prevent unnecessary re-renders
    const memoizedAccordionToOpen = useMemo(() => accordionToOpen, [accordionToOpen]);
    const memoizedDispatch = useMemo(() => dispatch, [dispatch]);

    // Subscribe to service state changes for React state synchronization
    useEffect(() => {
        const unsubscribe = accordionService.subscribe(newState => {
            setOpenAccordions(newState);
        });

        // Initialize with current service state
        setOpenAccordions(accordionService.getAccordionState());

        return unsubscribe;
    }, [accordionService]);

    // Handle Redux accordion open requests - replicates original useAccordionState logic
    useEffect(() => {
        if (memoizedAccordionToOpen) {
            logDebug('DynamicForm EFFECT', `Received request to open accordion: ${memoizedAccordionToOpen}`);

            // Use service to handle the request
            accordionService.handleReduxAccordionRequest(memoizedAccordionToOpen);

            // Clear the Redux request
            memoizedDispatch(setAccordionToOpen(null));
        }
    }, [memoizedAccordionToOpen, memoizedDispatch, accordionService]);

    // Memoized toggle handler that uses the service
    const handleAccordionToggle = useCallback(
        (groupName: string) => {
            const newState = accordionService.toggleAccordion(groupName);
            logDebug('DynamicForm TOGGLE', `User toggled ${groupName} to ${newState ? 'open' : 'closed'}`);
        },
        [accordionService]
    );

    return {
        openAccordions,
        handleAccordionToggle,
    };
};

/**
 * Legacy export for backward compatibility
 * This allows existing code to import useAccordionState and get the new implementation
 */
export const useAccordionState = useAccordionManagement;

export default useAccordionManagement;
